import React, { useEffect, useState } from "react";
import { ChevronLeftIcon, PencilIcon, XIcon } from "lucide-react-native";

import { ExpensesForm } from "@/blocks/ExpensesForm";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCard } from "@/components/VnCard";
import {
  VnDialog,
  VnDialogContent,
  VnDialogHeader,
  VnDialogPortal,
} from "@/components/VnDialog";
import { VnIcon } from "@/components/VnIcon";
import { VnScrollView } from "@/components/VnScrollView";
import { VnText } from "@/components/VnText/VnText";
import { useUpdateHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations";
import {
  createEmptyExpensesData,
  ExpenseTypeEnum,
  expenseTypeOptions,
  HouseholdMemberType,
} from "@/domain/householdMember";
import { formatCurrency } from "@/utils/currency";
import {
  formatHouseholdMemberName,
  hasExpenses,
  hasExpensesConfig,
} from "@/utils/householdMember";

type ExpenseItemConfigType = {
  expenseType: ExpenseTypeEnum;
  fieldName: keyof HouseholdMemberType;
};

const expenseTypeFieldMap: ExpenseItemConfigType[] = [
  {
    expenseType: ExpenseTypeEnum.STUDENT_LOANS,
    fieldName: "studentLoansMonthlyExpense",
  },
  {
    expenseType: ExpenseTypeEnum.TUITION_FEES,
    fieldName: "tuitionFeesMonthlyExpense",
  },
  {
    expenseType: ExpenseTypeEnum.ALIMONY_PAID,
    fieldName: "alimonyPaidMonthlyExpense",
  },
  {
    expenseType: ExpenseTypeEnum.MOVING_EXPENSES,
    fieldName: "movingMonthlyExpense",
  },
  {
    expenseType: ExpenseTypeEnum.IRA_CONTRIBUTIONS,
    fieldName: "iraContributionsMonthlyExpense",
  },
  {
    expenseType: ExpenseTypeEnum.CHILDCARE,
    fieldName: "childcareMonthlyExpense",
  },
  {
    expenseType: ExpenseTypeEnum.MEDICAL_EXPENSES,
    fieldName: "medicalExpenses3MonthsHelpNeededStatus",
  },
  { expenseType: ExpenseTypeEnum.OTHER, fieldName: "otherMonthlyExpense" },
];

type ExpenseItemPropsType = {
  member: HouseholdMemberType;
  onClickEditExpense: (expenseType: ExpenseTypeEnum) => void;
};

const ExpenseItems: React.FC<ExpenseItemPropsType> = ({
  member,
  onClickEditExpense,
}) => {
  return (
    <VnBox className="gap-1">
      {expenseTypeFieldMap.map(({ expenseType, fieldName }) => {
        const amount = member[fieldName] as number | string | null;
        if (amount === null) return null;
        const numericAmount =
          typeof amount === "string" ? parseFloat(amount) : amount;
        if (isNaN(numericAmount)) return null;
        return (
          <VnBox
            key={`${member.id}-${fieldName}`}
            className="bg-background mt-3 gap-2.5 rounded-md p-4">
            <VnBox className="flex flex-row items-center justify-between">
              <VnText
                variant="lg"
                i18nKey={`native:applicationExpensesScreen.expenseTypes.${expenseType}`}
              />
              <VnButton
                variant="ghost"
                onPress={() => onClickEditExpense(expenseType)}>
                <VnIcon as={PencilIcon} />
              </VnButton>
            </VnBox>
            <VnText
              variant="lg"
              className="text-muted-foreground"
              i18nKey="native:applicationExpensesScreen.monthlyExpense"
              i18nOptions={{
                monthlyExpense: formatCurrency(numericAmount),
              }}
            />
          </VnBox>
        );
      })}
      {member.medicalExpenses3MonthsHelpNeededStatus != null && (
        <VnBox className="bg-secondary mt-3 gap-2.5 rounded-md p-4">
          <VnBox className="flex flex-row items-center justify-between">
            <VnText
              variant="lg"
              i18nKey="native:applicationExpensesScreen.expenseTypes.MEDICAL_EXPENSES"
            />
            <VnButton
              variant="ghost"
              onPress={() =>
                onClickEditExpense(ExpenseTypeEnum.MEDICAL_EXPENSES)
              }>
              <VnIcon as={PencilIcon} />
            </VnButton>
          </VnBox>
          <VnText
            variant="lg"
            className="text-muted-foreground"
            i18nKey={
              member.medicalExpenses3MonthsHelpNeededStatus ===
              "MEDICAL_EXPENSES"
                ? "common:yes"
                : "common:no"
            }
          />
        </VnBox>
      )}
    </VnBox>
  );
};

type ExpensesCardPropsType = {
  member: HouseholdMemberType;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onClose: () => void;
};

export const ExpensesCard: React.FC<ExpensesCardPropsType> = ({
  member,
  isExpanded,
  onToggleExpand,
  onClose,
}) => {
  const [selectedExpenseType, setSelectedExpenseType] =
    useState<ExpenseTypeEnum | null>(null);

  const [isEditMode, setIsEditMode] = useState(false);

  const { mutate: updateHouseholdMember } = useUpdateHouseholdMemberMutation();

  const handleAddNoneExpense = () => {
    const emptyExpensesData = createEmptyExpensesData();
    updateHouseholdMember(
      {
        id: member.id,
        householdMemberData: emptyExpensesData,
      },
      {
        onSuccess: onToggleExpand,
      },
    );
  };

  const handleRemoveNoneExpense = () => {
    updateHouseholdMember(
      {
        id: member.id,
        householdMemberData: { hasExpense: null },
      },
      {
        onSuccess: onToggleExpand,
      },
    );
  };

  const handleSelectExpenseType = (expenseType: ExpenseTypeEnum) => {
    if (expenseType === ExpenseTypeEnum.NONE) {
      if (member.hasExpense === false) {
        onToggleExpand();
        return;
      }
      handleAddNoneExpense();
      return;
    }
    setSelectedExpenseType(expenseType);
  };

  const onClickEditExpense = (expenseType: ExpenseTypeEnum) => {
    setSelectedExpenseType(expenseType);
    setIsEditMode(true);
    onToggleExpand();
  };

  useEffect(() => {
    if (!isExpanded) {
      setSelectedExpenseType(null);
      setIsEditMode(false);
    }
  }, [isExpanded]);

  const hasMemberExpenses = hasExpenses(member);
  const hasMemberExpensesConfig = hasExpensesConfig(member);

  return (
    <VnCard className="gap-1 rounded-md p-4">
      <VnBox className="flex flex-row items-center justify-between">
        <VnText variant="lgMedium">{formatHouseholdMemberName(member)}</VnText>
        <VnButton
          variant={hasMemberExpensesConfig ? "outline" : "default"}
          onPress={onToggleExpand}
          i18nKey={
            hasMemberExpensesConfig
              ? "native:applicationExpensesScreen.addAnotherExpense"
              : "native:applicationExpensesScreen.addExpense"
          }
        />
      </VnBox>
      {member.hasExpense === false && (
        <VnBox className="bg-secondary mt-3 gap-2.5 rounded-md p-4">
          <VnBox className="flex flex-row items-center justify-between">
            <VnText
              variant="lg"
              i18nKey="native:applicationExpensesScreen.expenseTypes.NONE"
            />
            <VnButton variant="ghost" onPress={handleRemoveNoneExpense}>
              <VnIcon as={XIcon} />
            </VnButton>
          </VnBox>
        </VnBox>
      )}
      {member.hasExpense !== false &&
        (hasMemberExpenses ||
          member.medicalExpenses3MonthsHelpNeededStatus != null) && (
          <ExpenseItems
            member={member}
            onClickEditExpense={onClickEditExpense}
          />
        )}
      <VnDialog open={isExpanded} onOpenChange={onToggleExpand}>
        <VnDialogPortal>
          <VnDialogContent className="bg-background m-0 flex h-[100dvh] w-screen max-w-none flex-col gap-0 overflow-y-auto rounded-none px-0 py-4 sm:m-auto sm:h-auto sm:max-h-[90dvh] sm:max-w-[480px] sm:rounded-lg">
            <VnDialogHeader className="relative shrink-0 flex-row items-center justify-center px-4">
              {selectedExpenseType ? (
                <VnButton
                  variant="outline"
                  size="icon"
                  onPress={() => setSelectedExpenseType(null)}>
                  <VnIcon as={ChevronLeftIcon} />
                </VnButton>
              ) : (
                <VnBox className="h-10 w-10 sm:h-9 sm:w-9" />
              )}
              <VnText variant="headerTwo" className="flex-1 text-center">
                {formatHouseholdMemberName(member)}
              </VnText>
            </VnDialogHeader>
            {selectedExpenseType === null ? (
              <VnBox className="px-4">
                <VnText
                  variant="lg"
                  className="text-muted-foreground mb-4 shrink-0 text-center"
                  i18nKey="native:applicationExpensesScreen.memberDialog.title"
                />
                <VnScrollView showsVerticalScrollIndicator>
                  <VnBox className="gap-3">
                    {expenseTypeOptions.map(option => (
                      <VnButton
                        key={option.value}
                        variant="outline"
                        i18nKey={option.i18nKey}
                        className="border-border justify-start px-4 py-6"
                        onPress={() => handleSelectExpenseType(option.value)}
                      />
                    ))}
                  </VnBox>
                </VnScrollView>
              </VnBox>
            ) : (
              <ExpensesForm
                member={member}
                selectedExpenseType={selectedExpenseType}
                onClose={onClose}
                isEditMode={isEditMode}
              />
            )}
          </VnDialogContent>
        </VnDialogPortal>
      </VnDialog>
    </VnCard>
  );
};
